/**
 * @author David Roman <david@inarix.com>
 * @file Signin Component
 * @desc Created on 2023-09-17 12:00:44 pm
 * @copyright Inarix
 */

import axios from "axios";
import { API } from "../../../config";
import { State } from "../../slices";

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  isPortalVersionDifferent,
  isViewerOnlyInfo,
  setCompanyName,
  setOrgId,
  setPortalVersion,
  setToken,
} from "../../slices/authentication/authentication";
import { RootState } from "../../../configureStore";
import { requestAccessUrl, userLoginUrl } from "../../utils/queries";
import localforage from "localforage";
import { logOut } from "../../slices/layout";
import pjson from "../../../../package.json";
import { applyUserFacingDeletionFilter } from "../dashboard/dashboardDataAsync.actions";
import { applyUserFacingDeletionFilterDataTable } from "../dataTable/DataTableAsyncActions";

const PortalVersionJSON = pjson.version;
export interface User {
  token: string;
}

export interface TokenPayload {
  userId?: string;
  tokenId?: number;
  realmId: number;
  orgIds: number[];
  isSuperAdmin: boolean;
  expires: number;
  isTPToken: boolean;
}

const rememberme = localStorage?.getItem("rememberme");

export const haveAccess = createAsyncThunk(
  "AuthReducer/haveAccess",
  async (arg: { token: string; locale: string }, store) => {
    try {
      const response = await axios.get(requestAccessUrl, {
        headers: {
          Authorization: `Bearer ${arg.token}`,
          "Accept-Language": `${arg.locale}`,
        },
      });
      const { data } = response;

      store.dispatch(isViewerOnlyInfo(data?.isViewerOnly ?? false));
      return store.fulfillWithValue(data);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const orgFetch = createAsyncThunk(
  "AuthReducer/orgFetch",
  async (arg: { realmId: number; token: string; locale: string }, store) => {
    try {
      const response = await axios.get(
        `${API}/users/organization/${arg.realmId}`,
        {
          headers: {
            Authorization: `Bearer ${arg.token}`,
            "Accept-Language": `${arg.locale}`,
          },
        },
      );
      const { data } = response;

      store.dispatch(setCompanyName(data.name));
      store.dispatch(setOrgId(data.id));
      return store.fulfillWithValue(data);
    } catch (error) {
      store.dispatch(setCompanyName(null));
      return store.rejectWithValue(error);
    }
  },
);

export const haveAccess2 = async (
  token: string,
  locale: string,
): Promise<any> => {
  const res = await axios({
    method: "GET",
    url: requestAccessUrl,

    headers: {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    },
  });
  const { data } = res;
  return data;
};

export const signIn = createAsyncThunk(
  "AuthReducer/signIn",
  async (_: null | undefined, store) => {
    const { username, password } = (store.getState() as RootState)
      .Authentication;
    const { locale } = (store.getState() as State).LayoutReducer;

    try {
      const response = await axios.post(
        userLoginUrl,
        { username, password },
        {
          headers: {
            "Accept-Language": `${locale}`,
          },
        },
      );
      const { data } = response;

      store.dispatch(haveAccess({ token: data.token, locale }));
      store.dispatch(applyUserFacingDeletionFilter());
      store.dispatch(applyUserFacingDeletionFilterDataTable());
      await haveAccess2(data.token, locale);

      store.dispatch(setToken(data.token as string));

      if (data.token.length > 0) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error: any) {
      const currentUser = (store.getState() as RootState).Authentication.user;

      // Only dispatch logOut if user exists to avoid unnecessary REVERT_ALL
      if (currentUser !== null) {
        store.dispatch(logOut());
      }

      localStorage.removeItem("access_token");
      if (rememberme === "false") {
        localStorage.removeItem("password");
        localStorage.removeItem("username");
      }
      setTimeout(() => {
        localStorage.removeItem("persist:root");
        localforage.removeItem("persist:root");
      }, 1000);

      return store.rejectWithValue(error.response.data.message);
    }
  },
);

export const recoverPassword = createAsyncThunk(
  "AuthReducer/recoverPassword",
  async (_: null | undefined, store) => {
    const { recoverEmail } = (store.getState() as RootState).Authentication;

    try {
      const response = await axios.post(`${API}/users/request-password`, {
        input: recoverEmail,
      });
      const { data } = response;
      return store.fulfillWithValue(data);
    } catch (error: any) {
      return store.rejectWithValue(error);
    }
  },
);

export const resetPassword = createAsyncThunk(
  "AuthReducer/resetPassword",
  async (arg: { token: string; uuid: string }, store) => {
    const { newPassword } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;

    try {
      const response = await axios.patch(
        `${API}/users/request-password/${arg.uuid}`,
        { password: newPassword, token: arg.token },
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${arg.token}`,
          },
        },
      );

      const { data } = response;

      return store.fulfillWithValue(data);
    } catch (error: any) {
      return store.rejectWithValue(error);
    }
  },
);

export const refreshToken = createAsyncThunk(
  "AuthReducer/refreshToken",
  async (_: null | undefined, store) => {
    const { user } = (store.getState() as State).Authentication;
    const { locale } = (store.getState() as State).LayoutReducer;
    const headers = {
      Authorization: `Bearer ${user?.token}`,
      "Accept-Language": `${locale}`,
    };

    try {
      const response = await axios.put(
        `${API}/users/refresh-token/`,
        {},
        { headers },
      );
      const { data } = response;
      return store.fulfillWithValue(data?.token);
    } catch (error: any) {
      return store.rejectWithValue(error);
    }
  },
);

export const autoRefreshToken = createAsyncThunk(
  "AuthReducer/autoRefreshToken",
  async (_: null | undefined, store) => {
    const { refreshTimeoutId } = (store.getState() as State).Authentication;

    if (typeof refreshTimeoutId === "number") {
      window.clearTimeout(refreshTimeoutId);
    }

    return store.dispatch(refreshToken());
  },
);

export const handleVersionUpdate = createAsyncThunk(
  "AuthReducer/handleVersionUpdate",

  async (_: null, store) => {
    try {
      store.dispatch(setPortalVersion(PortalVersionJSON));

      store.dispatch(isPortalVersionDifferent(false));

      await new Promise((resolve) => setTimeout(resolve, 100));

      await localforage.clear();
      sessionStorage.clear();

      if ("caches" in globalThis) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName)),
        );
      }

      globalThis.location.reload();
    } catch (error) {
      globalThis.location.reload();
    }
  },
);
